import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { JsonEditorComponent, JsonEditorOptions } from 'ang-jsoneditor';
import { SchemaRevision, SchemaVersion } from 'src/app/Modules/management/models/instance/project-type.model';
import { GlobalEmittersService } from '../../../../../../services/global-emitters.service';
import { UniqueNameValidation } from 'src/app/Modules/management/core/Validators/unique-name.validator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InstanceDataService } from '../../../../../../services/instance-data.service';
import { TaggedItemType } from 'src/app/Modules/management/models/instance/instance.model';
import { AbstractControl } from '@angular/forms';
import { ValidationErrors } from '@angular/forms';
import { ValidatorFn } from '@angular/forms';
import { CustomToastService } from 'src/app/services/custom-toast.service';

@Component({
    selector: 'app-view-hierarchy-details',
    templateUrl: './view-hierarchy-details.component.html',
    styleUrls: ['./view-hierarchy-details.component.scss'],
})
export class ViewHierarchyDetailsComponent implements OnInit, OnDestroy {
    @Input()
    set taggedItem(value: TaggedItemType) {
        if (value) {
            this._taggedItem = value;
            this.setIntializeForm();
        }
    }

    get taggedItem(): TaggedItemType {
        return this._taggedItem;
    }

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild('editor') editor: JsonEditorComponent;
    destroy$: Subject<any>;

    assetTypeName: string;
    instanceName: string;
    taggedItemForm: FormGroup;
    taggedItemVersionList: SchemaVersion[];
    displayVersionList: SchemaVersion[];
    editorOptions: JsonEditorOptions;
    updatedSchema: string;
    isVersionAdd: boolean;
    currentPageItemLength: number;
    pageSize: number;
    taggedItemNamesList: string[];
    isAllowEdit: boolean;
    isCustom: boolean;
    private _taggedItem: TaggedItemType;

    constructor(
        private formBuilder: FormBuilder,
        public ngbActiveModal: NgbActiveModal,
        private globalEmittersService: GlobalEmittersService,
        private instanceDataService: InstanceDataService,
        private cd: ChangeDetectorRef,
        private toastrService: CustomToastService
    ) {
        this.taggedItemVersionList = [];
        this.displayVersionList = [];
        this.isVersionAdd = false;
        this.pageSize = 4;
        this.taggedItemNamesList = [];
        this.isAllowEdit = false;
        this.destroy$ = new Subject();
    }

    ngOnInit(): void {
        this.initializeSubscription();
        this.taggedItem ? this.globalEmittersService?.activeTaggedItemType.next(JSON.parse(JSON.stringify(this.taggedItem))) : '';
    }

    setIntializeForm() {
        const isCustom = this.taggedItem?.name?.toLowerCase() == null || this.taggedItem?.name?.toLowerCase() == undefined;

        this.taggedItemForm = this.formBuilder.group({
            name: new FormControl(this.taggedItem?.name, [
                Validators.required,
                UniqueNameValidation(this.taggedItemNamesList?.filter((item) => item !== this.taggedItem?.name)),
                Validators.required,
                conditionalForbiddenNameValidator(isCustom),
            ]),
            description: new FormControl(this.taggedItem?.description, Validators.required),
            canallocate: new FormControl(this.taggedItem?.canAllocate ?? false, Validators.nullValidator),
        });

        // * handling  tagged item version
        if (this.taggedItem?.schemaVersions) {
            // TODO display the version list
            this.taggedItem?.schemaVersions.forEach((version) => {
                this.taggedItemVersionList.push({
                    schemaVersionId: version?.schemaVersionId,
                    version: version?.version,
                    schema: JSON.parse(version?.schema),
                });
            });
        } else if (!!this.taggedItem.schemaRevision) {
            const version = new SchemaVersion();
            version.schema = JSON.parse(this.taggedItem?.schemaRevision?.schema);
            version.version = 1;
            this.taggedItemVersionList = [...this?.taggedItemVersionList, version];
            this.updatedSchema = version?.schema;
        }
        this.currentPageItemLength = this.taggedItemVersionList?.length;
        this.displayVersionList = this.taggedItemVersionList?.slice().reverse().slice(0, 4);
    }

    initializeSubscription() {
        this.globalEmittersService.taggedItemNamesList.pipe(takeUntil(this.destroy$)).subscribe((res) => {
            if (res) {
                this.taggedItemNamesList = res;
            }
        });

        this.globalEmittersService.taggedItemAllowEdit.pipe(takeUntil(this.destroy$)).subscribe((res) => {
            if (res) {
                this.isAllowEdit = res;
            } else if (res == false) {
                this.taggedItemForm.disable();
            }
        });

        this.instanceDataService.currentInstanceName.pipe(takeUntil(this.destroy$)).subscribe((res) => {
            if (res) {
                this.instanceName = res;
            }
        });

        this.instanceDataService.currentAssetType.pipe(takeUntil(this.destroy$)).subscribe((res) => {
            if (res) {
                this.assetTypeName = res;
            }
        });
    }

    // * method to add new version
    addNewVersion(): void {
        this.taggedItemForm.markAsDirty();
        if (this.taggedItem) {
            this.isVersionAdd = true; // * version added
            const version = new SchemaVersion();
            version.schema = JSON.parse(this.taggedItem?.schemaVersions[this.taggedItem?.schemaVersions?.length - 1]?.schema)
                ? JSON.parse(this.taggedItem?.schemaVersions[this.taggedItem?.schemaVersions.length - 1]?.schema)
                : '{}';
            version.version = this.taggedItemVersionList?.length + 1;
            this.taggedItemVersionList = [...this.taggedItemVersionList, version];
            this.updatedSchema = version?.schema;
        }
        if (this.taggedItemVersionList.length > 4) {
            this.currentPageItemLength = this.taggedItemVersionList?.length;
            this.cd.detectChanges();
            this.paginator.firstPage();
        }
        this.displayVersionList = this.taggedItemVersionList.slice().reverse().slice(0, 4);
        this.addPrimaryNameValidator();
    }

    // * helper method to get the schema data
    getSchemaData(schemaVersionId: string) {
        if (schemaVersionId) {
            let schema = this.taggedItemVersionList.find((schema) => schema.schemaVersionId == schemaVersionId);
            return schema.schema;
        } else {
            return this.taggedItemVersionList[this.taggedItemVersionList?.length - 1].schema;
        }
    }

    getJsonEditorOptions(schemaVersion: SchemaVersion) {
        this.editorOptions = new JsonEditorOptions();
        this.editorOptions.modes = ['tree', 'view', 'code', 'text'];
        // (this.editorOptions as any).worker = null;
        // ✅ Enable necessary UI components
        this.editorOptions.mainMenuBar = true; // Enables the menu bar
        this.editorOptions.navigationBar = true; // Enables the navigation bar
        this.editorOptions.statusBar = true; // Enables the status bar

        if (schemaVersion.schemaVersionId) {
            // * return json options with validation not to edit the the schema
            this.editorOptions.onEditable = () => {
                return false;
            };
        } else {
            // * return json options with  allow to edit the the schema
            this.editorOptions.onEditable = () => {
                return true;
            };

            this.editorOptions.onChangeText = (event) => {
                this.updatedSchema = event;

                if (!this.editor.isValidJson()) {
                    this.taggedItemForm.setErrors({
                        inValidSchema: 'Schema is not valid',
                    });
                } else {
                    this.taggedItemForm.setErrors(null);
                }
            };

            if (this.editor?.isValidJson()) {
                this.addPrimaryNameValidator();
            }
        }

        return this.editorOptions;
    }

    // * save taggedItem data
    saveTaggedItemData() {
        // if (this.isCustom) {
        //     let isExists = this.diagramShapes.find((m) => m.text === this.taggedItemForm.value.name && m.key != this.id);
        //     if (isExists) {
        //         this.toastrService.warningToast('Tagged item type already exists!!');
        //     }
        // }
        this.taggedItem.name = this.taggedItemForm.value.name;
        this.taggedItem.description = this.taggedItemForm.value.description;
        if (this.isVersionAdd) {
            const schemaRevision = new SchemaRevision();
            schemaRevision.schema = typeof this.updatedSchema == 'object' ? JSON.stringify(this.updatedSchema) : this.updatedSchema;
            this.taggedItem.schemaRevision = schemaRevision;
        } else if (!this.taggedItem?.taggedItemTypeId) {
            this.taggedItem.schemaRevision.schema = typeof this.updatedSchema == 'object' ? JSON.stringify(this.updatedSchema) : this.updatedSchema;
        }

        this.ngbActiveModal.close(this.taggedItem);
    }

    // * pagination handler
    getPaginationData(event: PageEvent) {
        this.displayVersionList = this.taggedItemVersionList
            .slice()
            .reverse()
            .slice(event.pageIndex * event.pageSize, event.pageIndex * event.pageSize + event.pageSize);
        return event;
    }

    canAllocatechange(event: any) {
        this.taggedItem.canAllocate = event.checked ? true : false;
    }

    // Function to validate the JSON data
    addPrimaryNameValidator() {
        if (this.editor) {
            let json = this.editor?.get();

            if (json) {
                const errorMessage = this.validatePrimaryName(json);
                if (errorMessage) {
                    this.taggedItemForm.setErrors({
                        isNamePrimaryTwice: "The attribute 'isnameprimary' can be defined only once within a schema.",
                    });
                } else {
                    this.taggedItemForm.setErrors(null);
                }
            }
        }
    }

    validatePrimaryName(jsonData: any): string | null {
        let primaryNameCount = 0;

        // Recursive helper function to search within fields
        const checkPrimaryName = (obj: any) => {
            if (Array.isArray(obj)) {
                obj.forEach((item) => checkPrimaryName(item));
            } else if (typeof obj === 'object' && obj !== null) {
                Object.keys(obj).forEach((key) => {
                    if (key === 'isnameprimary' && obj[key] === 'true') {
                        primaryNameCount++;
                    }
                    checkPrimaryName(obj[key]); // Recurse into the object
                });
            }
        };

        // Start validation from the root
        checkPrimaryName(jsonData);

        // Check if 'isnameprimary' appears more than once
        if (primaryNameCount > 1) {
            return 'Error: "isnameprimary": "true" appears more than once!';
        }

        return null; // No error
    }

    isSchemaValid(): boolean {
        try {
            const parsedSchema = JSON.parse(this.updatedSchema);

            if ((this.updatedSchema && this.editor.isValidJson()) || Object.keys(parsedSchema).length === 0) {
                return false;
            } else {
                return true;
            }
        } catch (error) {
            return true;
        }
    }
    closeClick() {
        this.ngbActiveModal.close(false);
    }
    ngOnDestroy(): void {
        this.updatedSchema = '';
        this.destroy$.unsubscribe();
    }
}

export function conditionalForbiddenNameValidator(isCustomType: boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!isCustomType) return null;

        const forbiddenNames = ['system', 'subsystem', 'sub-system', 'line', 'joint'];
        const value = control.value?.toLowerCase().trim();

        if (value && forbiddenNames.includes(value)) {
            return { forbiddenName: { value } };
        }

        return null;
    };
}
